import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Box, Button, LinearProgress, Link, Step, StepLabel, Stepper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useSignup } from '../../../hooks/use-signup.hook';
import { SignupRequestDTO } from '../../../types/authentication.dto';
import { useIsDesktop } from '../../../../shared/public/hooks/use-is-desktop.hook';
import { PUBLIC_PANELS } from '../../../../shared/public/constants/panel.constants';
import IdentityStep from './IdentityStep.component';
import DeliveryStep from './DeliveryStep.component';
import SummaryStep from './SummaryStep.component';
import { clientAccentOverrides } from '../authentication-form.styles';

interface SignupStepperProps {
  onSuccess: () => void;
  onNavigate: (panel: string) => void;
}

const STEPS = ['Identité', 'Livraison', 'Récapitulatif'];

const STEP_FIELDS: Array<Array<keyof SignupRequestDTO>> = [
  ['firstname', 'lastname', 'email', 'password'],
  ['phoneNumber', 'address', 'information'],
  [],
];

const DEFAULT_VALUES: SignupRequestDTO = {
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  phoneNumber: '',
  address: '',
  information: '',
  newsLetter: false,
};

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    ...clientAccentOverrides(theme),
  },
  stepper: { padding: theme.spacing(1, 0, 2) },
  mobileIndicator: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  progress: {
    borderRadius: 4,
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: theme.palette.client.primary.main,
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  submit: {
    backgroundColor: theme.palette.client.primary.main,
    color: '#fff',
    minHeight: 48,
  },
  links: { display: 'flex', flexDirection: 'column', gap: theme.spacing(1), marginTop: theme.spacing(1) },
}));

const SignupStepper: React.FC<SignupStepperProps> = ({ onSuccess, onNavigate }) => {
  const classes = useStyles();
  const isDesktop = useIsDesktop();
  const [activeStep, setActiveStep] = useState(0);
  const { isSubmitting, error, submit } = useSignup();

  const { control, handleSubmit, trigger, watch, setValue } = useForm<SignupRequestDTO>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  });

  const goToNextStep = async () => {
    const isValid = await trigger(STEP_FIELDS[activeStep]);

    if (isValid) setActiveStep((previousStep) => previousStep + 1);
  };

  const goToPreviousStep = () => setActiveStep((previousStep) => Math.max(0, previousStep - 1));

  const goToStep = (step: number) => setActiveStep(step);

  const onSubmit = handleSubmit(async (values) => {
    const user = await submit(values);

    if (user) onSuccess();
  });

  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <form className={classes.form} onSubmit={onSubmit} noValidate>
      {isDesktop ? (
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      ) : (
        <div className={classes.mobileIndicator}>
          <Typography variant="body2">{`Étape ${activeStep + 1} sur ${STEPS.length}`}</Typography>
          <LinearProgress
            className={classes.progress}
            variant="determinate"
            value={((activeStep + 1) / STEPS.length) * 100}
          />
        </div>
      )}

      {activeStep === 0 && <IdentityStep control={control} watch={watch} setValue={setValue} />}
      {activeStep === 1 && <DeliveryStep control={control} watch={watch} setValue={setValue} />}
      {activeStep === 2 && <SummaryStep watch={watch} onEditStep={goToStep} />}

      <Box aria-live="polite">
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </Box>

      <div className={classes.actions}>
        {activeStep > 0 ? (
          <Button type="button" onClick={goToPreviousStep} disabled={isSubmitting}>
            Précédent
          </Button>
        ) : (
          <span />
        )}
        {!isLastStep && (
          <Button type="button" variant="contained" className={classes.submit} onClick={goToNextStep}>
            Suivant
          </Button>
        )}
        {isLastStep && (
          <Button type="submit" variant="contained" className={classes.submit} disabled={isSubmitting}>
            Créer mon compte
          </Button>
        )}
      </div>

      <div className={classes.links}>
        <Link component="button" type="button" onClick={() => onNavigate(PUBLIC_PANELS.LOGIN)}>
          Se connecter
        </Link>
      </div>
    </form>
  );
};

export default SignupStepper;
